import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Trash2, FileText, Edit, Search, Package, Eye, ArrowRightCircle, Save, FileDown, Bell, Calendar, Phone, UserPlus, MessageCircle, MapPin, User, Percent, X, Ban, CheckCircle } from 'lucide-react';
import { formatPhone, formatDateDisplay, searchMatch, formatCurrency } from '@/utils/formatters';
import { ProductModal } from '@/components/ProductModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const QUOTE_STATUS_COLORS = {
  'pendente': 'bg-yellow-500/10 text-yellow-500 border-yellow-500',
  'aprovado': 'bg-green-500/10 text-green-500 border-green-500',
  'rejeitado': 'bg-destructive/10 text-destructive border-destructive',
  'convertido': 'bg-blue-500/10 text-blue-500 border-blue-500',
  'cancelado': 'bg-gray-500/10 text-gray-500 border-gray-500'
};

const FORMAS_PAGAMENTO = ['PIX', 'Dinheiro', 'Cartão de Crédito', 'Cartão de Débito', 'Transferência', 'Boleto', 'Cheque'];

// Função para gerar link do WhatsApp
const getWhatsAppLink = (telefone, mensagem = '') => {
  if (!telefone) return null;
  // Remove tudo que não for número
  const numero = telefone.replace(/\D/g, '');
  // Adiciona código do país se não tiver
  const numeroFormatado = numero.startsWith('55') ? numero : `55${numero}`;
  const msg = encodeURIComponent(mensagem);
  return `https://wa.me/${numeroFormatado}${msg ? `?text=${msg}` : ''}`;
};

export default function QuotesTab({ products, customers, onQuoteConverted, onCustomerCreated }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [viewingQuote, setViewingQuote] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingFollowUps, setPendingFollowUps] = useState([]);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertingQuote, setConvertingQuote] = useState(null);
  const [selectedOrcamento, setSelectedOrcamento] = useState('a');
  const [quoteFilter, setQuoteFilter] = useState('pendentes'); // 'pendentes', 'convertidos', 'cancelados', 'todos'

  // Form states
  const [formData, setFormData] = useState({
    cliente_id: '',
    cliente_nome: '',
    cliente_endereco: '',
    cliente_telefone: '',
    cliente_cpf_cnpj: '',
    // Orçamento A
    produtos_a: [],
    madeira_a: '',
    observacoes_a: '',
    desconto_tipo_a: '',
    desconto_valor_a: '',
    nome_tabela_a: 'Orçamento A',
    // Orçamento B
    produtos_b: [],
    madeira_b: '',
    observacoes_b: '',
    desconto_tipo_b: '',
    desconto_valor_b: '',
    nome_tabela_b: 'Orçamento B',
    // Pagamentos
    pagamentos: [],
    // Follow-ups
    follow_ups: [],
    validade_dias: 30
  });

  const [activeOrcamento, setActiveOrcamento] = useState('a');
  const [clienteMode, setClienteMode] = useState('existente');
  const [clienteSearch, setClienteSearch] = useState('');
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);
  const [highlightedClienteIndex, setHighlightedClienteIndex] = useState(-1);
  const [produtoSearch, setProdutoSearch] = useState('');
  const [showProdutoDropdown, setShowProdutoDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [produtoMode, setProdutoMode] = useState('catalogo');
  const [localProducts, setLocalProducts] = useState(products);
  const [localCustomers, setLocalCustomers] = useState(customers);
  const [productCategories, setProductCategories] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false); // Modal de cadastro completo
  const [tiposMadeira, setTiposMadeira] = useState([]);

  // Novo cliente inline
  const [novoCliente, setNovoCliente] = useState({ nome: '', telefone: '', email: '', endereco: '', cpf_cnpj: '', tipo: 'PF' });

  // Novo produto inline
  const [novoProduto, setNovoProduto] = useState({ nome: '', custo: '', preco: '', categoria: '', unidade: 'UN', observacoes: '' });
  const [produtoAtual, setProdutoAtual] = useState({ produto_id: '', nome: '', quantidade: 1, preco_unitario: 0, custo_unitario: 0 });

  // Novo pagamento
  const [novoPagamento, setNovoPagamento] = useState({ data: '', forma: '', valor: '' });

  // Novo follow-up
  const [novoFollowUp, setNovoFollowUp] = useState({ data: '', observacao: '' });

  useEffect(() => {
    setLocalProducts(products);
  }, [products]);

  useEffect(() => {
    setLocalCustomers(customers);
  }, [customers]);

  // Memoizar clientes filtrados para evitar recalcular a cada render
  const filteredCustomers = useMemo(() => {
    if (!clienteSearch) return [];
    return localCustomers.filter(c => 
      searchMatch(c.nome, clienteSearch) || c.telefone?.includes(clienteSearch)
    ).slice(0, 10);
  }, [localCustomers, clienteSearch]);

  useEffect(() => {
    fetchQuotes();
    fetchCategories();
    fetchPendingFollowUps();
    fetchTiposMadeira();
  }, []);

  const fetchTiposMadeira = async () => {
    try {
      const res = await axios.get(`${API}/tipos-madeira`);
      setTiposMadeira(res.data || []);
    } catch (error) {
      console.error('Erro ao carregar tipos de madeira:', error);
    }
  };

  const fetchQuotes = async () => {
    try {
      const res = await axios.get(`${API}/quotes`);
      setQuotes(res.data);
    } catch (error) {
      toast.error('Erro ao carregar orçamentos');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/settings/categories/product`);
      setProductCategories(res.data.categories || []);
    } catch (error) {
      console.error('Erro ao carregar categorias');
    }
  };

  const fetchPendingFollowUps = async () => {
    try {
      const res = await axios.get(`${API}/quotes/follow-ups/pending`);
      setPendingFollowUps(res.data);
    } catch (error) {
      console.error('Erro ao carregar follow-ups');
    }
  };

  const handleProdutoChange = (produtoId) => {
    const produto = localProducts.find(p => p.id === produtoId);
    if (produto) {
      setProdutoAtual({
        produto_id: produto.id,
        nome: produto.nome,
        quantidade: 1,
        preco_unitario: produto.preco_base,
        custo_unitario: produto.custo || 0
      });
    }
  };

  const adicionarProduto = async () => {
    const key = activeOrcamento === 'a' ? 'produtos_a' : 'produtos_b';
    
    if (!produtoAtual.nome) {
      toast.error('Selecione um produto');
      return;
    }
    setFormData({
      ...formData,
      [key]: [...formData[key], { ...produtoAtual }]
    });
    setProdutoAtual({ produto_id: '', nome: '', quantidade: 1, preco_unitario: 0, custo_unitario: 0 });
    setProdutoSearch('');
  };

  const removerProduto = (orcamento, index) => {
    const key = orcamento === 'a' ? 'produtos_a' : 'produtos_b';
    setFormData({
      ...formData,
      [key]: formData[key].filter((_, i) => i !== index)
    });
  };

  const adicionarPagamento = () => {
    if (!novoPagamento.data || !novoPagamento.forma || !novoPagamento.valor) {
      toast.error('Preencha todos os campos do pagamento');
      return;
    }
    setFormData({
      ...formData,
      pagamentos: [...formData.pagamentos, { ...novoPagamento, valor: parseFloat(novoPagamento.valor) }]
    });
    setNovoPagamento({ data: '', forma: '', valor: '' });
  };

  const removerPagamento = (index) => {
    setFormData({
      ...formData,
      pagamentos: formData.pagamentos.filter((_, i) => i !== index)
    });
  };

  const adicionarFollowUp = () => {
    if (!novoFollowUp.data) {
      toast.error('Selecione uma data para o lembrete');
      return;
    }
    setFormData({
      ...formData,
      follow_ups: [...formData.follow_ups, { ...novoFollowUp, realizado: false }]
    });
    setNovoFollowUp({ data: '', observacao: '' });
  };

  const removerFollowUp = (index) => {
    setFormData({
      ...formData,
      follow_ups: formData.follow_ups.filter((_, i) => i !== index)
    });
  };

  const cadastrarCliente = async () => {
    if (!novoCliente.nome) {
      toast.error('Nome do cliente é obrigatório');
      return;
    }
    try {
      const res = await axios.post(`${API}/customers`, {
        nome: novoCliente.nome,
        telefone: novoCliente.telefone,
        email: novoCliente.email,
        endereco: novoCliente.endereco,
        cpf_cnpj: novoCliente.cpf_cnpj,
        tipo: novoCliente.tipo || 'PF'
      });
      toast.success('Cliente cadastrado!');
      setFormData({
        ...formData,
        cliente_id: res.data.id,
        cliente_nome: res.data.nome,
        cliente_telefone: res.data.telefone || '',
        cliente_endereco: res.data.endereco || '',
        cliente_cpf_cnpj: res.data.cpf_cnpj || ''
      });
      setLocalCustomers([...localCustomers, res.data]);
      if (onCustomerCreated) onCustomerCreated(res.data);
      setClienteMode('existente');
      setClienteSearch(res.data.nome);
      setNovoCliente({ nome: '', telefone: '', email: '', endereco: '', cpf_cnpj: '', tipo: 'PF' });
    } catch (error) {
      toast.error('Erro ao cadastrar cliente');
    }
  };

  const calcularTotal = (produtos) => {
    return produtos.reduce((sum, p) => sum + (p.preco_unitario * p.quantidade), 0);
  };

  const resetForm = () => {
    setFormData({
      cliente_id: '', cliente_nome: '', cliente_endereco: '', cliente_telefone: '', cliente_cpf_cnpj: '',
      produtos_a: [], madeira_a: '', observacoes_a: '', desconto_tipo_a: '', desconto_valor_a: '', nome_tabela_a: 'Orçamento A',
      produtos_b: [], madeira_b: '', observacoes_b: '', desconto_tipo_b: '', desconto_valor_b: '', nome_tabela_b: 'Orçamento B',
      pagamentos: [], follow_ups: [], validade_dias: 30
    });
    setProdutoAtual({ produto_id: '', nome: '', quantidade: 1, preco_unitario: 0, custo_unitario: 0 });
    setClienteMode('existente');
    setClienteSearch('');
    setProdutoSearch('');
    setProdutoMode('catalogo');
    setActiveOrcamento('a');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.produtos_a.length === 0) {
      toast.error('Adicione pelo menos um produto no Orçamento A');
      return;
    }
    if (!formData.cliente_nome) {
      toast.error('Informe o nome do cliente');
      return;
    }

    try {
      const payload = {
        cliente_id: formData.cliente_id || undefined,
        cliente_nome: formData.cliente_nome,
        cliente_endereco: formData.cliente_endereco || undefined,
        cliente_telefone: formData.cliente_telefone || undefined,
        cliente_cpf_cnpj: formData.cliente_cpf_cnpj || undefined,
        produtos_a: formData.produtos_a,
        madeira_a: formData.madeira_a || undefined,
        observacoes_a: formData.observacoes_a,  // Enviar mesmo se vazio para poder apagar
        desconto_tipo_a: formData.desconto_tipo_a || undefined,
        desconto_valor_a: formData.desconto_valor_a ? parseFloat(formData.desconto_valor_a) : undefined,
        nome_tabela_a: formData.nome_tabela_a || 'Orçamento A',
        produtos_b: formData.produtos_b.length > 0 ? formData.produtos_b : undefined,
        madeira_b: formData.madeira_b || undefined,
        observacoes_b: formData.observacoes_b,  // Enviar mesmo se vazio para poder apagar
        desconto_tipo_b: formData.desconto_tipo_b || undefined,
        desconto_valor_b: formData.desconto_valor_b ? parseFloat(formData.desconto_valor_b) : undefined,
        nome_tabela_b: formData.produtos_b.length > 0 ? (formData.nome_tabela_b || 'Orçamento B') : undefined,
        pagamentos: formData.pagamentos.length > 0 ? formData.pagamentos : undefined,
        follow_ups: formData.follow_ups.length > 0 ? formData.follow_ups : undefined,
        validade_dias: parseInt(formData.validade_dias) || 30
      };

      if (editingQuote) {
        await axios.put(`${API}/quotes/${editingQuote.id}`, payload);
        toast.success('Orçamento atualizado!');
      } else {
        await axios.post(`${API}/quotes`, payload);
        toast.success('Orçamento criado!');
      }

      setDialogOpen(false);
      setEditingQuote(null);
      fetchQuotes();
      fetchPendingFollowUps();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar orçamento');
    }
  };

  const handleEdit = (quote) => {
    setEditingQuote(quote);
    setFormData({
      cliente_id: quote.cliente_id || '',
      cliente_nome: quote.cliente_nome || '',
      cliente_endereco: quote.cliente_endereco || '',
      cliente_telefone: quote.cliente_telefone || '',
      cliente_cpf_cnpj: quote.cliente_cpf_cnpj || '',
      produtos_a: quote.produtos_a || quote.produtos || [],
      madeira_a: quote.madeira_a || quote.madeira || '',
      observacoes_a: quote.observacoes_a || quote.observacoes || '',
      desconto_tipo_a: quote.desconto_tipo_a || quote.desconto_tipo || '',
      desconto_valor_a: (quote.desconto_valor_a || quote.desconto_valor)?.toString() || '',
      nome_tabela_a: quote.nome_tabela_a || 'Orçamento A',
      produtos_b: quote.produtos_b || [],
      madeira_b: quote.madeira_b || '',
      observacoes_b: quote.observacoes_b || '',
      desconto_tipo_b: quote.desconto_tipo_b || '',
      desconto_valor_b: quote.desconto_valor_b?.toString() || '',
      nome_tabela_b: quote.nome_tabela_b || 'Orçamento B',
      pagamentos: quote.pagamentos || [],
      follow_ups: quote.follow_ups || [],
      validade_dias: quote.validade_dias || 30
    });
    if (quote.cliente_id) {
      setClienteMode('existente');
      setClienteSearch(quote.cliente_nome);
    } else {
      setClienteMode('manual');
    }
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja excluir este orçamento?')) return;
    try {
      await axios.delete(`${API}/quotes/${id}`);
      toast.success('Orçamento excluído!');
      fetchQuotes();
    } catch (error) {
      toast.error('Erro ao excluir orçamento');
    }
  };

  const handlePrintPDF = async (quoteId) => {
    try {
      // Buscar dados do orçamento para montar o nome do arquivo
      const quote = quotes.find(q => q.id === quoteId);
      const numeroOrcamento = quote?.numero_orcamento || `ORC-${quoteId.slice(0, 8)}`;
      const clienteNome = quote?.cliente_nome || 'Cliente';
      // Limpar nome do cliente para nome de arquivo válido
      const clienteNomeFile = clienteNome.replace(/[/\\:*?"<>|]/g, '-');
      const filename = `${numeroOrcamento} - ${clienteNomeFile}.pdf`;
      
      const response = await axios.get(`${API}/quotes/${quoteId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF gerado!');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
    }
  };

  const openConvertDialog = (quote) => {
    setConvertingQuote(quote);
    setSelectedOrcamento('a');
    setConvertDialogOpen(true);
  };

  const handleConvertToOrder = async () => {
    if (!convertingQuote) return;
    try {
      const res = await axios.post(`${API}/quotes/${convertingQuote.id}/convert-to-order?orcamento=${selectedOrcamento}`);
      toast.success(`Pedido ${res.data.numero_pedido} criado!`);
      setConvertDialogOpen(false);
      setConvertingQuote(null);
      fetchQuotes();
      if (onQuoteConverted) onQuoteConverted();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao converter');
    }
  };

  const handleUpdateStatus = async (quoteId, status) => {
    try {
      await axios.put(`${API}/quotes/${quoteId}`, { status });
      toast.success('Status atualizado!');
      fetchQuotes();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleCancelQuote = async (quoteId) => {
    if (!confirm('Tem certeza que deseja cancelar este orçamento?')) return;
    try {
      await axios.put(`${API}/quotes/${quoteId}`, { status: 'cancelado' });
      toast.success('Orçamento cancelado!');
      fetchQuotes();
    } catch (error) {
      toast.error('Erro ao cancelar orçamento');
    }
  };

  const markFollowUpDone = async (quoteId, data) => {
    try {
      await axios.post(`${API}/quotes/${quoteId}/mark-follow-up?data=${data}`);
      toast.success('Lembrete marcado como realizado!');
      fetchPendingFollowUps();
    } catch (error) {
      toast.error('Erro ao atualizar lembrete');
    }
  };

  // Filtrar orçamentos por busca e status
  const filteredQuotes = useMemo(() => {
    return quotes.filter(q => {
      // Filtro de busca
      const matchSearch = searchMatch(q.cliente_nome, searchTerm) ||
        q.numero_orcamento?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filtro de status
      let matchStatus = true;
      if (quoteFilter === 'pendentes') {
        matchStatus = q.status === 'pendente' || q.status === 'aprovado';
      } else if (quoteFilter === 'convertidos') {
        matchStatus = q.status === 'convertido';
      } else if (quoteFilter === 'cancelados') {
        matchStatus = q.status === 'cancelado' || q.status === 'rejeitado';
      }
      // 'todos' não filtra por status
      
      return matchSearch && matchStatus;
    });
  }, [quotes, searchTerm, quoteFilter]);
  
  // Contadores por status
  const quoteCounts = useMemo(() => ({
    pendentes: quotes.filter(q => q.status === 'pendente' || q.status === 'aprovado').length,
    convertidos: quotes.filter(q => q.status === 'convertido').length,
    cancelados: quotes.filter(q => q.status === 'cancelado' || q.status === 'rejeitado').length,
    todos: quotes.length
  }), [quotes]);

  const isExpired = (dataValidade) => {
    if (!dataValidade) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [year, month, day] = dataValidade.split('-').map(Number);
    const validade = new Date(year, month - 1, day);
    return validade < today;
  };

  const todayFollowUps = pendingFollowUps.filter(f => f.is_today);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando orçamentos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Lembretes do dia */}
      {todayFollowUps.length > 0 && (
        <Card className="border-yellow-500 bg-yellow-500/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Bell className="h-5 w-5 text-yellow-500" />
              <span className="font-bold text-yellow-600">Lembretes de Hoje ({todayFollowUps.length})</span>
            </div>
            <div className="space-y-2">
              {todayFollowUps.map((fu, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-background rounded border">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{fu.numero_orcamento}</span>
                    <span>-</span>
                    <span>{fu.cliente_nome}</span>
                    {fu.cliente_telefone && (
                      <>
                        <a href={`tel:${fu.cliente_telefone}`} className="text-primary text-sm flex items-center gap-1">
                          <Phone className="h-3 w-3" />{fu.cliente_telefone}
                        </a>
                        <a 
                          href={getWhatsAppLink(fu.cliente_telefone, `Olá ${fu.cliente_nome}! Tudo bem? Estou entrando em contato sobre o orçamento ${fu.numero_orcamento} da Serraria Guedes.`)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-green-500 hover:text-green-600"
                          title="Abrir WhatsApp"
                        >
                          <MessageCircle className="h-4 w-4" />
                        </a>
                      </>
                    )}
                    {fu.observacao && <span className="text-muted-foreground text-sm">({fu.observacao})</span>}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => markFollowUpDone(fu.quote_id, fu.data)}>
                    Marcar como feito
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por cliente ou número..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="quotes-search"
          />
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="new-quote-btn">
          <Plus className="mr-2 h-4 w-4" /> Novo Orçamento
        </Button>
      </div>

      {/* Tabs de filtro por status */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={quoteFilter === 'pendentes' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setQuoteFilter('pendentes')}
          className="flex items-center gap-2"
        >
          <Bell className="h-4 w-4" />
          Pendentes ({quoteCounts.pendentes})
        </Button>
        <Button
          variant={quoteFilter === 'convertidos' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setQuoteFilter('convertidos')}
          className="flex items-center gap-2"
        >
          <CheckCircle className="h-4 w-4 text-green-500" />
          Convertidos ({quoteCounts.convertidos})
        </Button>
        <Button
          variant={quoteFilter === 'cancelados' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setQuoteFilter('cancelados')}
          className="flex items-center gap-2"
        >
          <Ban className="h-4 w-4 text-gray-500" />
          Cancelados ({quoteCounts.cancelados})
        </Button>
        <Button
          variant={quoteFilter === 'todos' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setQuoteFilter('todos')}
        >
          Todos ({quoteCounts.todos})
        </Button>
      </div>

      {/* Dialog de Novo/Editar Orçamento */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingQuote(null); resetForm(); } }}>
          <DialogContent className="w-full max-w-[95vw] sm:max-w-4xl max-h-[85vh] sm:max-h-[90vh] overflow-y-auto overflow-x-hidden pb-8 mb-6 sm:mb-0 rounded-2xl sm:rounded-lg">
            <DialogHeader>
              <DialogTitle>{editingQuote ? 'Editar Orçamento' : 'Novo Orçamento'}</DialogTitle>
              <DialogDescription>Preencha os dados do orçamento</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* 1. Cliente - DESTACADO */}
              <div className="border-2 border-primary/30 rounded-lg p-4 bg-primary/5">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <User className="h-5 w-5 text-primary" />
                    Cliente
                  </Label>
                  <div className="flex gap-1">
                    <Button type="button" variant={clienteMode === 'existente' ? 'default' : 'outline'} size="sm" onClick={() => setClienteMode('existente')} className="text-xs px-2">
                      Existente
                    </Button>
                    <Button type="button" variant={clienteMode === 'manual' ? 'default' : 'outline'} size="sm" onClick={() => setClienteMode('manual')} className="text-xs px-2">
                      Manual
                    </Button>
                    <Button type="button" variant={clienteMode === 'novo' ? 'default' : 'outline'} size="sm" onClick={() => setClienteMode('novo')} className="text-xs px-2">
                      + Novo
                    </Button>
                  </div>
                </div>

                {clienteMode === 'existente' && (
                  <div className="relative">
                    <Input 
                      placeholder="Pesquisar cliente..." 
                      value={clienteSearch} 
                      onChange={(e) => { 
                        setClienteSearch(e.target.value); 
                        setShowClienteDropdown(true); 
                        setHighlightedClienteIndex(-1);
                      }} 
                      onFocus={() => {
                        setShowClienteDropdown(true);
                        setHighlightedClienteIndex(-1);
                      }}
                      onKeyDown={(e) => {
                        const totalItems = filteredCustomers.length;
                        
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          const newIndex = highlightedClienteIndex < totalItems - 1 ? highlightedClienteIndex + 1 : 0;
                          setHighlightedClienteIndex(newIndex);
                          setTimeout(() => {
                            const el = document.querySelector(`[data-quote-cliente-index="${newIndex}"]`);
                            if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                          }, 0);
                        } else if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          const newIndex = highlightedClienteIndex > 0 ? highlightedClienteIndex - 1 : totalItems - 1;
                          setHighlightedClienteIndex(newIndex);
                          setTimeout(() => {
                            const el = document.querySelector(`[data-quote-cliente-index="${newIndex}"]`);
                            if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                          }, 0);
                        } else if (e.key === 'Enter' && highlightedClienteIndex >= 0 && showClienteDropdown) {
                          e.preventDefault();
                          const cliente = filteredCustomers[highlightedClienteIndex];
                          if (cliente) {
                            setFormData({ ...formData, cliente_id: cliente.id, cliente_nome: cliente.nome, cliente_endereco: cliente.endereco || '', cliente_telefone: cliente.telefone || '' });
                            setClienteSearch(cliente.nome);
                            setShowClienteDropdown(false);
                            setHighlightedClienteIndex(-1);
                          }
                        } else if (e.key === 'Escape') {
                          setShowClienteDropdown(false);
                          setHighlightedClienteIndex(-1);
                        }
                      }}
                    />
                    {showClienteDropdown && clienteSearch && (
                      <div className="absolute z-[100] w-full mt-1 bg-background border rounded-lg shadow-lg max-h-[300px] overflow-y-auto">
                        {filteredCustomers.map((c, idx) => (
                          <div 
                            key={c.id} 
                            data-quote-cliente-index={idx}
                            className={`px-4 py-4 cursor-pointer ${highlightedClienteIndex === idx ? 'bg-primary/20' : 'hover:bg-muted/50'} active:bg-muted`} 
                            onClick={() => {
                              setFormData({ ...formData, cliente_id: c.id, cliente_nome: c.nome, cliente_endereco: c.endereco || '', cliente_telefone: c.telefone || '' });
                              setClienteSearch(c.nome);
                              setShowClienteDropdown(false);
                              setHighlightedClienteIndex(-1);
                            }}>
                            <div className="font-semibold">{c.nome}</div>
                            <div className="text-sm text-muted-foreground">{c.telefone}</div>
                          </div>
                        ))}
                        {filteredCustomers.length === 0 && (
                          <div className="px-4 py-4 text-muted-foreground">
                            Nenhum cliente encontrado
                          </div>
                        )}
                      </div>
                    )}
                    {formData.cliente_id && (
                      <div className="mt-2 p-2 bg-muted rounded flex justify-between items-center">
                        <span className="text-sm">Selecionado: <strong>{formData.cliente_nome}</strong></span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => { setFormData({ ...formData, cliente_id: '', cliente_nome: '', cliente_endereco: '', cliente_telefone: '' }); setClienteSearch(''); }}>Limpar</Button>
                      </div>
                    )}
                  </div>
                )}

                {clienteMode === 'manual' && (
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder="Nome *" value={formData.cliente_nome} onChange={(e) => setFormData({ ...formData, cliente_nome: e.target.value })} />
                    <Input placeholder="Telefone" value={formData.cliente_telefone} onChange={(e) => setFormData({ ...formData, cliente_telefone: formatPhone(e.target.value) })} />
                    <Input className="col-span-2" placeholder="Endereço" value={formData.cliente_endereco} onChange={(e) => setFormData({ ...formData, cliente_endereco: e.target.value })} />
                  </div>
                )}

                {clienteMode === 'novo' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 bg-muted/50 rounded border">
                    <Input
                      placeholder="Nome *"
                      value={novoCliente.nome}
                      onChange={(e) => setNovoCliente({ ...novoCliente, nome: e.target.value })}
                      required
                    />
                    <Input
                      placeholder="Telefone"
                      value={novoCliente.telefone}
                      onChange={(e) => setNovoCliente({ ...novoCliente, telefone: formatPhone(e.target.value) })}
                    />
                    <Input
                      placeholder="Email"
                      value={novoCliente.email || ''}
                      onChange={(e) => setNovoCliente({ ...novoCliente, email: e.target.value })}
                    />
                    <Input
                      placeholder="CPF/CNPJ"
                      value={novoCliente.cpf_cnpj}
                      onChange={(e) => setNovoCliente({ ...novoCliente, cpf_cnpj: e.target.value })}
                    />
                    <Input
                      placeholder="Endereço"
                      value={novoCliente.endereco}
                      onChange={(e) => setNovoCliente({ ...novoCliente, endereco: e.target.value })}
                      className="sm:col-span-2"
                    />
                    <select
                      className="w-full p-2 border rounded bg-background text-base md:text-sm"
                      value={novoCliente.tipo || 'PF'}
                      onChange={(e) => setNovoCliente({ ...novoCliente, tipo: e.target.value })}
                    >
                      <option value="PF">Pessoa Física</option>
                      <option value="PJ">Pessoa Jurídica</option>
                    </select>
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="w-full"
                      onClick={cadastrarCliente}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Salvar Cliente
                    </Button>
                    <p className="sm:col-span-2 text-xs text-muted-foreground">
                      Clique em "Salvar Cliente" para cadastrar antes de criar o orçamento.
                    </p>
                  </div>
                )}
              </div>

              {/* 2. Abas Orçamento A / B */}
              <Tabs value={activeOrcamento} onValueChange={setActiveOrcamento}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="a" className="text-xs sm:text-sm">{formData.nome_tabela_a || 'Orçamento A'} {formData.produtos_a.length > 0 && `(${formData.produtos_a.length})`}</TabsTrigger>
                  <TabsTrigger value="b" className="text-xs sm:text-sm">{formData.nome_tabela_b || 'Orçamento B'} {formData.produtos_b.length > 0 && `(${formData.produtos_b.length})`}</TabsTrigger>
                </TabsList>

                {['a', 'b'].map(orc => (
                  <TabsContent key={orc} value={orc} className="space-y-4 mt-2">
                    {/* Nome da tabela personalizado */}
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Label className="text-xs mb-1 block">Nome da Tabela</Label>
                        <Input 
                          placeholder={`Orçamento ${orc.toUpperCase()}`} 
                          value={formData[`nome_tabela_${orc}`] || ''} 
                          onChange={(e) => setFormData({ ...formData, [`nome_tabela_${orc}`]: e.target.value })} 
                        />
                      </div>
                    </div>
                    
                    {/* Madeira e Validade - Dentro de cada aba */}
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Label className="text-xs mb-1 block">Madeira</Label>
                        <Input placeholder="Ex: JATOBÁ" value={formData[`madeira_${orc}`]} onChange={(e) => setFormData({ ...formData, [`madeira_${orc}`]: e.target.value.toUpperCase() })} className="text-center" />
                      </div>
                      <div className="w-20">
                        <Label className="text-xs mb-1 block">Validade</Label>
                        <Input type="number" value={formData.validade_dias} onChange={(e) => setFormData({ ...formData, validade_dias: e.target.value })} className="text-center" />
                      </div>
                    </div>

                    {/* Produtos - DESTACADO */}
                    <div className="border-2 border-primary/30 rounded-lg p-4 bg-primary/5">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-base font-semibold flex items-center gap-2">
                          <Package className="h-5 w-5 text-primary" />
                          Produtos
                        </Label>
                        <div className="flex gap-1">
                          <Button type="button" variant={produtoMode === 'catalogo' ? 'default' : 'outline'} size="sm" onClick={() => setProdutoMode('catalogo')} className="text-xs px-2">Catálogo</Button>
                          <Button 
                            type="button" 
                            size="sm" 
                            variant="outline"
                            className="bg-emerald-500/10 border-emerald-500/50 text-emerald-600 hover:bg-emerald-500/20 text-xs px-2"
                            onClick={() => setShowProductModal(true)}
                            data-testid="btn-novo-produto-orcamento"
                          >
                            <Plus className="h-3 w-3 mr-1" />Novo Produto
                          </Button>
                        </div>
                      </div>

                      {/* Lista de produtos */}
                      {formData[`produtos_${orc}`].length > 0 && (
                        <div className="space-y-2 mb-3">
                          {formData[`produtos_${orc}`].map((p, idx) => (
                            <div key={idx} className="flex flex-col gap-2 p-2 bg-background rounded border">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                <span className="flex-1 truncate font-medium text-sm">{p.nome}</span>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <div className="flex items-center gap-1">
                                    <Input type="number" step="0.01" min="0.01" className="w-14 sm:w-16 h-8 text-sm" value={p.quantidade} onChange={(e) => {
                                      const newProds = [...formData[`produtos_${orc}`]];
                                      const val = e.target.value;
                                      newProds[idx] = { ...p, quantidade: val === '' ? '' : parseFloat(val) };
                                      setFormData({ ...formData, [`produtos_${orc}`]: newProds });
                                    }} />
                                    <span className="text-xs text-muted-foreground">x</span>
                                    <Input type="number" className="w-20 sm:w-24 h-8 text-sm" value={p.preco_unitario} onChange={(e) => {
                                      const newProds = [...formData[`produtos_${orc}`]];
                                      newProds[idx] = { ...p, preco_unitario: parseFloat(e.target.value) || 0 };
                                      setFormData({ ...formData, [`produtos_${orc}`]: newProds });
                                    }} />
                                  </div>
                                  <span className="text-sm font-mono">= R$ {((p.quantidade || 0) * (p.preco_unitario || 0)).toFixed(2)}</span>
                                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => removerProduto(orc, idx)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                                </div>
                              </div>
                              {/* Observações do produto */}
                              <div className="flex items-center gap-2">
                                <Input
                                  placeholder="Observações do produto (aparece no PDF)"
                                  className="flex-1 h-8 text-xs"
                                  value={p.observacoes || ''}
                                  onChange={(e) => {
                                    const newProds = [...formData[`produtos_${orc}`]];
                                    newProds[idx] = { ...p, observacoes: e.target.value };
                                    setFormData({ ...formData, [`produtos_${orc}`]: newProds });
                                  }}
                                />
                                {p.observacoes && (
                                  <Button 
                                    type="button" 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-8 w-8 shrink-0"
                                    onClick={() => {
                                      const newProds = [...formData[`produtos_${orc}`]];
                                      newProds[idx] = { ...p, observacoes: '' };
                                      setFormData({ ...formData, [`produtos_${orc}`]: newProds });
                                    }}
                                    title="Limpar observação"
                                  >
                                    <X className="h-3 w-3 text-muted-foreground" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Adicionar produto */}
                      <div className="space-y-2">
                        <div className="relative" style={{ zIndex: 9999 }}>
                          <Input 
                            placeholder="Pesquisar produto..." 
                            value={produtoSearch} 
                            onChange={(e) => { 
                              setProdutoSearch(e.target.value); 
                              setShowProdutoDropdown(true);
                              setHighlightedIndex(-1);
                            }} 
                            onFocus={() => setShowProdutoDropdown(true)}
                            onKeyDown={(e) => {
                              const filteredProducts = localProducts.filter(p => searchMatch(p.nome, produtoSearch)).slice(0, 10);
                              const totalItems = filteredProducts.length;
                              
                              if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                const newIndex = highlightedIndex < totalItems - 1 ? highlightedIndex + 1 : 0;
                                setHighlightedIndex(newIndex);
                                setTimeout(() => {
                                  const el = document.querySelector(`[data-quote-produto-index="${newIndex}"]`);
                                  if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                                }, 0);
                              } else if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                const newIndex = highlightedIndex > 0 ? highlightedIndex - 1 : totalItems - 1;
                                setHighlightedIndex(newIndex);
                                setTimeout(() => {
                                  const el = document.querySelector(`[data-quote-produto-index="${newIndex}"]`);
                                  if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                                }, 0);
                              } else if (e.key === 'Enter' && highlightedIndex >= 0 && showProdutoDropdown) {
                                e.preventDefault();
                                const selectedProduct = filteredProducts[highlightedIndex];
                                if (selectedProduct) {
                                  handleProdutoChange(selectedProduct.id);
                                  setProdutoSearch(selectedProduct.nome);
                                  setShowProdutoDropdown(false);
                                  setHighlightedIndex(-1);
                                }
                              } else if (e.key === 'Escape') {
                                setShowProdutoDropdown(false);
                                setHighlightedIndex(-1);
                              }
                            }}
                          />
                          {showProdutoDropdown && produtoSearch && (
                            <>
                              <div className="absolute left-0 right-0 mt-1 bg-background border rounded-lg shadow-2xl overflow-y-auto" style={{ zIndex: 99999, maxHeight: '250px' }}>
                                {localProducts.filter(p => searchMatch(p.nome, produtoSearch)).slice(0, 10).map((p, idx) => (
                                  <div 
                                    key={p.id}
                                    data-quote-produto-index={idx}
                                    className={`px-4 py-4 cursor-pointer ${highlightedIndex === idx ? 'bg-primary/20' : 'hover:bg-muted/50'} active:bg-muted`}
                                    onClick={() => { handleProdutoChange(p.id); setProdutoSearch(p.nome); setShowProdutoDropdown(false); }}
                                  >
                                    <div className="font-semibold">{p.nome}</div>
                                    <div className="text-sm text-muted-foreground">{p.categoria || 'Sem categoria'} • R$ {p.preco_base?.toFixed(2)}</div>
                                  </div>
                                ))}
                              </div>
                              <div style={{ height: '260px' }}></div>
                            </>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <Input type="number" step="0.01" min="0.01" placeholder="Qtd" value={produtoAtual.quantidade} onChange={(e) => setProdutoAtual({ ...produtoAtual, quantidade: e.target.value === '' ? '' : parseFloat(e.target.value) })} onBlur={(e) => { if (e.target.value === '' || isNaN(parseFloat(e.target.value))) setProdutoAtual({ ...produtoAtual, quantidade: 1 }); }} />
                          <Input type="number" placeholder="Preço" value={produtoAtual.preco_unitario} onChange={(e) => setProdutoAtual({ ...produtoAtual, preco_unitario: parseFloat(e.target.value) || 0 })} />
                          <Button type="button" onClick={adicionarProduto}><Plus className="h-4 w-4" /></Button>
                        </div>
                      </div>
                    </div>

                    {/* Desconto simplificado */}
                    <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                      <Percent className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      <select className="p-2 border rounded bg-background text-sm min-w-[100px]" value={formData[`desconto_tipo_${orc}`]} onChange={(e) => setFormData({ ...formData, [`desconto_tipo_${orc}`]: e.target.value })}>
                        <option value="">Sem desconto</option>
                        <option value="percentual">%</option>
                        <option value="valor">R$</option>
                      </select>
                      <Input type="number" placeholder="Valor" value={formData[`desconto_valor_${orc}`]} onChange={(e) => setFormData({ ...formData, [`desconto_valor_${orc}`]: e.target.value })} disabled={!formData[`desconto_tipo_${orc}`]} className="w-24" />
                      {formData[`desconto_tipo_${orc}`] && formData[`desconto_valor_${orc}`] && (() => {
                        const subtotal = calcularTotal(formData[`produtos_${orc}`]);
                        const descontoValor = formData[`desconto_tipo_${orc}`] === 'percentual' 
                          ? (subtotal * parseFloat(formData[`desconto_valor_${orc}`]) / 100)
                          : parseFloat(formData[`desconto_valor_${orc}`]);
                        const percentual = subtotal > 0 ? (descontoValor / subtotal * 100).toFixed(1) : 0;
                        return (
                          <span className="font-bold text-destructive text-sm whitespace-nowrap">
                            -R$ {descontoValor.toFixed(2)}
                            {formData[`desconto_tipo_${orc}`] === 'valor' && subtotal > 0 && (
                              <span className="text-xs ml-1 opacity-70">({percentual}%)</span>
                            )}
                          </span>
                        );
                      })()}
                    </div>

                    {/* Subtotal e Total */}
                    {formData[`produtos_${orc}`].length > 0 && (
                      <div className="border rounded-lg p-4 bg-gradient-to-r from-green-500/10 to-transparent space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">Subtotal ({formData[`produtos_${orc}`].length} {formData[`produtos_${orc}`].length === 1 ? 'item' : 'itens'})</span>
                          <span className="font-mono">R$ {calcularTotal(formData[`produtos_${orc}`]).toFixed(2)}</span>
                        </div>
                        {formData[`desconto_tipo_${orc}`] && formData[`desconto_valor_${orc}`] && (() => {
                          const subtotal = calcularTotal(formData[`produtos_${orc}`]);
                          const descontoValor = formData[`desconto_tipo_${orc}`] === 'percentual' 
                            ? (subtotal * parseFloat(formData[`desconto_valor_${orc}`]) / 100)
                            : parseFloat(formData[`desconto_valor_${orc}`]);
                          const percentual = subtotal > 0 ? (descontoValor / subtotal * 100).toFixed(1) : 0;
                          return (
                            <div className="flex justify-between items-center text-destructive">
                              <span>Desconto {formData[`desconto_tipo_${orc}`] === 'valor' && subtotal > 0 && (
                                <span className="text-xs opacity-70">({percentual}%)</span>
                              )}</span>
                              <span className="font-mono">-R$ {descontoValor.toFixed(2)}</span>
                            </div>
                          );
                        })()}
                        <div className="flex justify-between items-center pt-2 border-t">
                          <span className="text-lg font-semibold">Total</span>
                          <span className="text-2xl font-bold font-mono text-green-500">
                            R$ {(() => {
                              const subtotal = calcularTotal(formData[`produtos_${orc}`]);
                              const desconto = formData[`desconto_tipo_${orc}`] === 'percentual'
                                ? subtotal * parseFloat(formData[`desconto_valor_${orc}`] || 0) / 100
                                : parseFloat(formData[`desconto_valor_${orc}`] || 0);
                              return (subtotal - desconto).toFixed(2);
                            })()}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Observações */}
                    <div>
                      <Label className="text-xs mb-1 block">Observações</Label>
                      <Textarea placeholder="Observações do orçamento..." value={formData[`observacoes_${orc}`]} onChange={(e) => setFormData({ ...formData, [`observacoes_${orc}`]: e.target.value })} rows={2} />
                    </div>
                  </TabsContent>
                ))}
              </Tabs>

              {/* Pagamentos */}
              <div className="border p-4 rounded-lg space-y-3">
                <Label className="text-lg font-bold">Condições de Pagamento</Label>
                {formData.pagamentos.length > 0 && (
                  <Table>
                    <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Forma</TableHead><TableHead>Valor</TableHead><TableHead></TableHead></TableRow></TableHeader>
                    <TableBody>
                      {formData.pagamentos.map((p, i) => (
                        <TableRow key={i}>
                          <TableCell>{formatDateDisplay(p.data)}</TableCell>
                          <TableCell>{p.forma}</TableCell>
                          <TableCell>R$ {p.valor.toFixed(2)}</TableCell>
                          <TableCell><Button type="button" variant="ghost" size="icon" onClick={() => removerPagamento(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder=""
                      value={novoPagamento.data ? novoPagamento.data.split('-').reverse().join('/') : ''}
                      onChange={(e) => {
                        let v = e.target.value.replace(/\D/g, '');
                        if (v.length >= 2) v = v.slice(0,2) + '/' + v.slice(2);
                        if (v.length >= 5) v = v.slice(0,5) + '/' + v.slice(5,9);
                        e.target.value = v;
                        if (v.length === 10) {
                          const [d, m, y] = v.split('/');
                          setNovoPagamento({ ...novoPagamento, data: `${y}-${m}-${d}` });
                        }
                      }}
                      maxLength={10}
                      className="text-sm text-center"
                    />
                    <input
                      type="date"
                      value={novoPagamento.data}
                      onChange={(e) => setNovoPagamento({ ...novoPagamento, data: e.target.value })}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                  </div>
                  <select className="p-2 border rounded bg-background text-base md:text-sm" value={novoPagamento.forma} onChange={(e) => setNovoPagamento({ ...novoPagamento, forma: e.target.value })}>
                    <option value="">Forma...</option>
                    {FORMAS_PAGAMENTO.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <Input type="number" placeholder="Valor" value={novoPagamento.valor} onChange={(e) => setNovoPagamento({ ...novoPagamento, valor: e.target.value })} />
                  <Button type="button" onClick={adicionarPagamento}><Plus className="h-4 w-4 mr-1" /> Add</Button>
                </div>
              </div>

              {/* Follow-ups */}
              <div className="border p-4 rounded-lg space-y-3">
                <Label className="text-lg font-bold flex items-center gap-2"><Bell className="h-5 w-5" /> Lembretes de Contato</Label>
                <p className="text-sm text-muted-foreground">Adicione datas para lembrar de entrar em contato caso o cliente não feche</p>
                {formData.follow_ups.length > 0 && (
                  <div className="space-y-2">
                    {formData.follow_ups.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 bg-muted rounded">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDateDisplay(f.data)}</span>
                        {f.observacao && <span className="text-muted-foreground">- {f.observacao}</span>}
                        <Button type="button" variant="ghost" size="icon" className="ml-auto" onClick={() => removerFollowUp(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder=""
                      value={novoFollowUp.data ? novoFollowUp.data.split('-').reverse().join('/') : ''}
                      onChange={(e) => {
                        let v = e.target.value.replace(/\D/g, '');
                        if (v.length >= 2) v = v.slice(0,2) + '/' + v.slice(2);
                        if (v.length >= 5) v = v.slice(0,5) + '/' + v.slice(5,9);
                        e.target.value = v;
                        if (v.length === 10) {
                          const [d, m, y] = v.split('/');
                          setNovoFollowUp({ ...novoFollowUp, data: `${y}-${m}-${d}` });
                        }
                      }}
                      maxLength={10}
                      className="text-sm text-center"
                    />
                    <input
                      type="date"
                      value={novoFollowUp.data}
                      onChange={(e) => setNovoFollowUp({ ...novoFollowUp, data: e.target.value })}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                  </div>
                  <Input placeholder="Observação (opcional)" value={novoFollowUp.observacao} onChange={(e) => setNovoFollowUp({ ...novoFollowUp, observacao: e.target.value })} />
                  <Button type="button" onClick={adicionarFollowUp}><Plus className="h-4 w-4 mr-1" /> Lembrete</Button>
                </div>
              </div>

              <Button type="submit" className="w-full mb-4 sm:mb-0"><Save className="h-4 w-4 mr-2" /> {editingQuote ? 'Atualizar' : 'Criar'} Orçamento</Button>
            </form>
          </DialogContent>
        </Dialog>

      {/* Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
        <Card><CardContent className="p-3 sm:p-4"><p className="text-xs sm:text-sm text-muted-foreground">Total</p><p className="text-xl sm:text-2xl font-bold">{quotes.length}</p></CardContent></Card>
        <Card><CardContent className="p-3 sm:p-4"><p className="text-xs sm:text-sm text-muted-foreground">Pendentes</p><p className="text-xl sm:text-2xl font-bold text-yellow-500">{quotes.filter(q => q.status === 'pendente').length}</p></CardContent></Card>
        <Card><CardContent className="p-3 sm:p-4"><p className="text-xs sm:text-sm text-muted-foreground">Aprovados</p><p className="text-xl sm:text-2xl font-bold text-green-500">{quotes.filter(q => q.status === 'aprovado').length}</p></CardContent></Card>
        <Card><CardContent className="p-3 sm:p-4"><p className="text-xs sm:text-sm text-muted-foreground">Convertidos</p><p className="text-xl sm:text-2xl font-bold text-blue-500">{quotes.filter(q => q.status === 'convertido').length}</p></CardContent></Card>
      </div>

      {/* Lista */}
      {filteredQuotes.length > 0 ? (
        <Accordion type="single" collapsible className="space-y-3">
          {filteredQuotes.map(quote => {
            const temB = quote.produtos_b && quote.produtos_b.length > 0;
            const totalA = quote.total_com_desconto_a || quote.total_com_desconto || 0;
            const totalB = quote.total_com_desconto_b || 0;
            const isExpiredQuote = isExpired(quote.data_validade) && quote.status === 'pendente';
            
            return (
              <AccordionItem 
                key={quote.id} 
                value={quote.id}
                className={`border rounded-lg overflow-hidden ${
                  quote.status === 'convertido' ? 'bg-blue-500/10 border-blue-500' :
                  quote.status === 'aprovado' ? 'bg-green-500/10 border-green-500' :
                  quote.status === 'rejeitado' ? 'bg-red-500/10 border-red-500' :
                  isExpiredQuote ? 'bg-red-500/10 border-red-500' :
                  'bg-yellow-500/10 border-yellow-500'
                }`}
              >
                <AccordionTrigger className="hover:no-underline px-3 sm:px-4 py-2 sm:py-3">
                  <div className="flex flex-col w-full gap-1 text-left">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-bold text-sm sm:text-base text-primary">{quote.numero_orcamento}</span>
                        <Badge variant="outline" className={`text-xs ${QUOTE_STATUS_COLORS[quote.status]}`}>
                          {quote.status === 'pendente' ? 'Pendente' : 
                           quote.status === 'aprovado' ? 'Aprovado' :
                           quote.status === 'rejeitado' ? 'Rejeitado' : 'Convertido'}
                        </Badge>
                        {isExpiredQuote && <Badge variant="destructive" className="text-xs">Vencido</Badge>}
                      </div>
                      <span className="font-bold text-sm sm:text-lg text-primary whitespace-nowrap">
                        R$ {totalA.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs sm:text-sm text-muted-foreground">
                      <span className="font-medium text-foreground">{quote.cliente_nome}</span>
                      {quote.cliente_telefone && <span className="hidden sm:inline">{quote.cliente_telefone}</span>}
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-3 sm:px-4 pb-3 sm:pb-4">
                  <div className="space-y-3">
                    {/* Info do Cliente */}
                    <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                        <span>Criado: {formatDateDisplay(quote.createdAt?.split('T')[0])}</span>
                      </div>
                      {quote.data_validade && (
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                          <span className={isExpiredQuote ? 'text-red-500' : ''}>
                            Validade: {formatDateDisplay(quote.data_validade)}
                          </span>
                        </div>
                      )}
                      {quote.cliente_telefone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                          <span>{quote.cliente_telefone}</span>
                          <a 
                            href={getWhatsAppLink(quote.cliente_telefone, `Olá ${quote.cliente_nome}! Tudo bem? Estou entrando em contato sobre o orçamento ${quote.numero_orcamento} da Serraria Guedes.`)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-green-500 hover:text-green-600"
                            title="Abrir WhatsApp"
                          >
                            <MessageCircle className="h-4 w-4" />
                          </a>
                        </div>
                      )}
                      {quote.cliente_endereco && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                          <span className="truncate">{quote.cliente_endereco}</span>
                        </div>
                      )}
                    </div>

                    {/* Produtos Orçamento A */}
                    <div className="bg-muted/50 rounded-lg p-2 sm:p-3">
                      <h4 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                        <Package className="h-4 w-4" />
                        {temB ? 'Orçamento A' : 'Produtos'}
                      </h4>
                      <div className="space-y-1">
                        {(quote.produtos_a || quote.produtos || []).map((p, idx) => (
                          <div key={idx} className="flex justify-between text-xs sm:text-sm gap-2">
                            <span className="truncate">{p.quantidade}x {p.nome}</span>
                            <span className="font-mono whitespace-nowrap">R$ {(p.quantidade * p.preco_unitario).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        ))}
                        {quote.desconto_a > 0 && (
                          <div className="flex justify-between text-xs sm:text-sm text-red-500">
                            <span>Desconto</span>
                            <span>- R$ {quote.desconto_a.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        )}
                        <div className="flex justify-between font-bold border-t pt-1 mt-1 text-sm">
                          <span>Total {temB ? 'A' : ''}</span>
                          <span className="text-primary">R$ {totalA.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    </div>

                    {/* Produtos Orçamento B */}
                    {temB && (
                      <div className="bg-muted/50 rounded-lg p-2 sm:p-3">
                        <h4 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                          <Package className="h-4 w-4" />
                          Orçamento B
                        </h4>
                        <div className="space-y-1">
                          {quote.produtos_b.map((p, idx) => (
                            <div key={idx} className="flex justify-between text-xs sm:text-sm gap-2">
                              <span className="truncate">{p.quantidade}x {p.nome}</span>
                              <span className="font-mono whitespace-nowrap">R$ {(p.quantidade * p.preco_unitario).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          ))}
                          {quote.desconto_b > 0 && (
                            <div className="flex justify-between text-xs sm:text-sm text-red-500">
                              <span>Desconto</span>
                              <span>- R$ {quote.desconto_b.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          )}
                          <div className="flex justify-between font-bold border-t pt-1 mt-1 text-sm">
                            <span>Total B</span>
                            <span className="text-primary">R$ {totalB.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Observações */}
                    {quote.observacoes && (
                      <div className="text-xs sm:text-sm text-muted-foreground">
                        <strong>Obs:</strong> {quote.observacoes}
                      </div>
                    )}

                    {/* Ações */}
                    <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 pt-2 border-t">
                      <Select value={quote.status} onValueChange={(v) => handleUpdateStatus(quote.id, v)} disabled={quote.status === 'convertido'}>
                        <SelectTrigger className={`w-full sm:w-28 h-8 text-xs ${QUOTE_STATUS_COLORS[quote.status]}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pendente">Pendente</SelectItem>
                          <SelectItem value="aprovado">Aprovado</SelectItem>
                          <SelectItem value="rejeitado">Rejeitado</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => handlePrintPDF(quote.id)}>
                        <FileDown className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />PDF
                      </Button>
                      <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => handleEdit(quote)} disabled={quote.status === 'convertido'}>
                        <Edit className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />Editar
                      </Button>
                      <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setViewingQuote(quote)}>
                        <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />Ver
                      </Button>
                      {quote.status !== 'convertido' && quote.status !== 'rejeitado' && quote.status !== 'cancelado' && (
                        <Button variant="default" size="sm" className="h-8 text-xs col-span-2 sm:col-span-1" onClick={() => openConvertDialog(quote)}>
                          <ArrowRightCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />Converter
                        </Button>
                      )}
                      {quote.status !== 'convertido' && quote.status !== 'cancelado' && (
                        <Button variant="outline" size="sm" className="h-8 text-xs text-gray-500" onClick={() => handleCancelQuote(quote.id)}>
                          <Ban className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />Cancelar
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" className="h-8 text-xs text-destructive" onClick={() => handleDelete(quote.id)}>
                        <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />Excluir
                      </Button>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      ) : (
        <Card><CardContent className="py-16 text-center"><FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" /><p className="text-muted-foreground mb-4">{searchTerm ? 'Nenhum orçamento encontrado' : 'Nenhum orçamento'}</p><Button onClick={() => setDialogOpen(true)}><Plus className="h-4 w-4 mr-2" />Criar Primeiro</Button></CardContent></Card>
      )}

      {/* Dialog de Conversão */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-24 sm:pb-6 rounded-2xl sm:rounded-lg">
          <DialogHeader>
            <DialogTitle>Converter em Pedido</DialogTitle>
            <DialogDescription>Selecione qual orçamento converter em pedido</DialogDescription>
          </DialogHeader>
          {convertingQuote && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Selecione o orçamento:</Label>
                <select className="w-full p-2 border rounded bg-background text-base md:text-sm" value={selectedOrcamento} onChange={(e) => setSelectedOrcamento(e.target.value)}>
                  <option value="a">Orçamento A - R$ {(convertingQuote.total_com_desconto_a || convertingQuote.total_com_desconto || 0).toFixed(2)}</option>
                  {convertingQuote.produtos_b && convertingQuote.produtos_b.length > 0 && (
                    <>
                      <option value="b">Orçamento B - R$ {(convertingQuote.total_com_desconto_b || 0).toFixed(2)}</option>
                      <option value="ambos">Ambos (A + B)</option>
                    </>
                  )}
                </select>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setConvertDialogOpen(false)}>Cancelar</Button>
                <Button className="flex-1" onClick={handleConvertToOrder}><ArrowRightCircle className="h-4 w-4 mr-2" />Converter</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Visualização */}
      <Dialog open={!!viewingQuote} onOpenChange={(open) => !open && setViewingQuote(null)}>
        <DialogContent className="w-full max-w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto overflow-x-hidden pb-24 sm:pb-6 rounded-2xl sm:rounded-lg">
          {viewingQuote && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-primary" />
                  {viewingQuote.numero_orcamento}
                  <Badge className={QUOTE_STATUS_COLORS[viewingQuote.status]}>{viewingQuote.status}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div><p className="text-sm text-muted-foreground">Cliente</p><p className="font-medium">{viewingQuote.cliente_nome}</p></div>
                  <div><p className="text-sm text-muted-foreground">Telefone</p><p>{viewingQuote.cliente_telefone || '-'}</p></div>
                </div>
                {/* Produtos A */}
                <div>
                  <h4 className="font-bold mb-2">{viewingQuote.produtos_b?.length > 0 ? 'OPÇÃO A' : 'Produtos'}</h4>
                  <Table>
                    <TableHeader><TableRow><TableHead>Produto</TableHead><TableHead>Qtd</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {(viewingQuote.produtos_a || viewingQuote.produtos || []).map((p, i) => (
                        <TableRow key={i}><TableCell>{p.nome}</TableCell><TableCell>{p.quantidade}</TableCell><TableCell className="text-right font-mono">R$ {(p.quantidade * p.preco_unitario).toFixed(2)}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <p className="text-right font-bold mt-2">Total A: R$ {(viewingQuote.total_com_desconto_a || viewingQuote.total_com_desconto || 0).toFixed(2)}</p>
                </div>
                {/* Produtos B */}
                {viewingQuote.produtos_b?.length > 0 && (
                  <div>
                    <h4 className="font-bold mb-2">OPÇÃO B</h4>
                    <Table>
                      <TableHeader><TableRow><TableHead>Produto</TableHead><TableHead>Qtd</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {viewingQuote.produtos_b.map((p, i) => (
                          <TableRow key={i}><TableCell>{p.nome}</TableCell><TableCell>{p.quantidade}</TableCell><TableCell className="text-right font-mono">R$ {(p.quantidade * p.preco_unitario).toFixed(2)}</TableCell></TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <p className="text-right font-bold mt-2">Total B: R$ {(viewingQuote.total_com_desconto_b || 0).toFixed(2)}</p>
                  </div>
                )}
                {/* Pagamentos */}
                {viewingQuote.pagamentos?.length > 0 && (
                  <div>
                    <h4 className="font-bold mb-2">Condições de Pagamento</h4>
                    <Table>
                      <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Forma</TableHead><TableHead className="text-right">Valor</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {viewingQuote.pagamentos.map((p, i) => (
                          <TableRow key={i}><TableCell>{formatDateDisplay(p.data)}</TableCell><TableCell>{p.forma}</TableCell><TableCell className="text-right font-mono">R$ {p.valor.toFixed(2)}</TableCell></TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                <div className="flex gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => handlePrintPDF(viewingQuote.id)}><FileDown className="h-4 w-4 mr-2" />PDF</Button>
                  {viewingQuote.status !== 'convertido' && <Button onClick={() => { openConvertDialog(viewingQuote); setViewingQuote(null); }}><ArrowRightCircle className="h-4 w-4 mr-2" />Converter</Button>}
                  <Button variant="secondary" onClick={() => setViewingQuote(null)}>Fechar</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Modal de Cadastro Completo de Produto */}
      <ProductModal
        open={showProductModal}
        onOpenChange={setShowProductModal}
        onProductCreated={(newProduct) => {
          // Adicionar produto à lista local
          setLocalProducts(prev => [...prev, newProduct]);
          // Selecionar o produto recém criado
          setProdutoAtual({
            produto_id: newProduct.id,
            nome: newProduct.nome,
            quantidade: 1,
            preco_unitario: newProduct.preco_base || 0,
            custo_unitario: newProduct.custo || 0
          });
          setProdutoSearch(newProduct.nome);
          setProdutoMode('catalogo');
          toast.success(`Produto "${newProduct.nome}" criado! Ajuste a quantidade e adicione ao orçamento.`);
        }}
        tiposMadeira={tiposMadeira}
        categories={productCategories}
      />
    </div>
  );
}
